import { fireToast } from '../../../../../helpers/Toasts';
import { accountsApi } from '../../../../../api/accountsApi';
import {
  SELLER_ANALYTICS_CATEGORIES,
  SELLER_ANALYTICS_TIME_RANGES,
} from '../../../../../types';
import { formatAuthorizationHeader } from '../../../../../helpers/auth';
import { pushToDataLayer } from '../../../../../services/gtmService';
import { useAdDetailsContext } from '../../../../../contexts/AdDetailsContext';
import {
  useUserContext,
  useAuthOptions,
} from '../../../../../contexts/UserContext';
import type { useAdTitleBoxProps } from '../../../../../components/PageWrappers/AdDetails/components/AdTitleBox/AdTitleBox.typed';
import { logger } from '@dist-marketplace-frontend/services';
import { PAGE } from '../../../../../helpers/pages';
import { getPriceDisplay } from '../../../../../helpers/getPriceDisplay';
import { buildFinanceUrl } from './buildFinanceUrl';

const useAdTitleBox = (props: useAdTitleBoxProps) => {
  const {
    adId,
    carFinance,
    currency,
    financeInfo,
    financeLink,
    price,
    priceOnRequest,
    sellerType,
    title,
    section,
    location,
    mainSection,
    trustedDealer,
    priceIndicator,
  } = props;

  const { accessToken, user } = useAuthOptions();
  const { setIsPricePerMonthModalOpen } = useAdDetailsContext();
  const { setIsSellerAnalyticsModalOpen, setSellerAnalyticsModalDisplay } =
    useUserContext();

  async function handleAnalyticsView() {
    try {
      const { data } = await accountsApi.getSellerAnalyticsData(
        adId,
        SELLER_ANALYTICS_CATEGORIES.AD_VIEWS,
        SELLER_ANALYTICS_TIME_RANGES.SEVEN_DAYS,
        formatAuthorizationHeader(accessToken),
      );
      setIsSellerAnalyticsModalOpen(true);
      setSellerAnalyticsModalDisplay({
        title,
        selectedTab: SELLER_ANALYTICS_CATEGORIES.AD_VIEWS,
        data: data.plotValues,
      });
      pushToDataLayer({
        event: 'seller_analytics',
        event_category: 'ad_management',
        event_action: 'view_ad_analytics',
        event_label: 'ad details page',
      });
    } catch (error) {
      logger.error({
        message: 'Error getting seller analytics data',
        error,
        tags: [PAGE.AD_DETAILS],
        customData: { functionName: 'handleAnalyticsView' },
      });
      fireToast({
        type: 'ERROR',
        text: 'Oops, something went wrong! Please try again later',
      });
    }
  }

  const hasFinanceLink = Boolean(financeLink || financeInfo);
  const finance = {
    text: financeLink?.title || 'Get Finance Approval',
    url: buildFinanceUrl({
      financeLink,
      financeInfo,
      priceIndicator,
      userId: user?.id,
      sellerType,
      section,
      location,
      mainSection,
      trustedDealer,
    }),
  };

  function handleFinanceLinkClick() {
    pushToDataLayer({
      event: 'partner_clicks',
      servicePartnerType: carFinance?.banner?.partnerType,
      servicePartnerName: carFinance?.banner?.partnerName,
    });
  }

  const financeClassImp = `${carFinance?.banner?.partnerName}-${carFinance?.banner?.partnerType}-imp`;

  const { priceText, isValidPrice } = getPriceDisplay({
    price,
    priceOnRequest: priceOnRequest ?? false,
    currency,
  });

  const isPriceOnRequest = !isValidPrice;

  const handlePPMClick = () => {
    setIsPricePerMonthModalOpen(true);
  };

  return {
    handleAnalyticsView,
    hasFinanceLink,
    finance,
    handleFinanceLinkClick,
    financeClassImp,
    priceText,
    isPriceOnRequest,
    handlePPMClick,
  };
};

export { useAdTitleBox };
